import { useAppDispatch, useAppSelector } from "@/core/app/hooks"
import { RootState } from "@/core/app/store"
import {
  stageControl,
  stagingIPR,
  stagingManager,
  viewControlInTreatment,
} from "@/gluelayer"
import {
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material"
import { useCallback, useEffect } from "react"
import SCCallbackManager, { StageCommon } from "../bottom/StageBar/stageCommon"
import { RightPage } from "../right/RightPage"
import {
  setOpenCollisionCheck,
  setOpenInitialFinal,
  setOpenStagingIPR,
  setStageCountZeroWarning,
  setStageDeleteAllPAWarning,
  setStageQuickFunc,
  setStageShowIpr,
  setStageShowMode,
  setStageShowType,
  setStageShowVelocity,
  setStageTwoButtonWarningMessage,
} from "../udTreatSlice"

const UDQuickStaging = (props) => {
  const dispatch = useAppDispatch()
  // const [stagefunc, setStageFunc] = React.useState("")
  const { stageShowMode, stageShowIpr, stageQuickFunc } = useAppSelector(
    (state: RootState) => state.udTreatService,
  )
  const {
    clinicalSettings: { NumberSystem },
  } = useAppSelector((state: RootState) => state.userService)

  const handleStageFucChange = async (event, newStageFunc) => {
    const checkResult = await checkingBeforeQuitIpr()
    if (checkResult === "cancel") {
      return
    }
    await quitStaging()
    goStaging(newStageFunc)
  }

  const handleClose = async () => {
    const checkResult = await checkingBeforeQuitIpr()
    if (checkResult === "cancel") {
      return
    }
    await quitStaging()
    props.onClose()
  }

  const checkingBeforeQuitIpr = useCallback(async () => {
    let checkResult = ""
    if (stageShowIpr) {
      const remainingIprStr = stagingIPR.checkRemainingIPR()
      if (remainingIprStr && remainingIprStr.length > 0) {
        dispatch(setStageTwoButtonWarningMessage(remainingIprStr))
        await StageCommon.getInstance()
          .stageTwoButtonWarningMessageResultListening()
          .then((result) => {
            checkResult = result
          })
        if (checkResult === "cancel") {
          return checkResult
        }
      }

      const nullIprNodes = stagingIPR.checkNullIPRNodes()
      console.info("nullIprNodes:", nullIprNodes)
      if (nullIprNodes && nullIprNodes.archType >= 0) {
        dispatch(
          setStageTwoButtonWarningMessage(
            "There is an IPR stage with no IPR specified. Would you like that stage to be deleted?",
          ),
        )
        await StageCommon.getInstance()
          .stageTwoButtonWarningMessageResultListening()
          .then((result) => {
            checkResult = result
          })
        if (checkResult === "cancel") {
          //jump to the stage which ipr is 0.0
          if (SCCallbackManager.getInstance().getExecCurrentStepCallBack()) {
            SCCallbackManager.getInstance().getExecCurrentStepCallBack()(
              nullIprNodes.stage,
            )
          }
          return checkResult
        } else if (checkResult === "ok") {
          stagingIPR.removeNullIprNodes()
        }
      }
    }
    return checkResult
  }, [stageShowIpr])

  const quitStaging = useCallback(async () => {
    // revert the state to default values
    dispatch(setStageShowType("normal"))
    dispatch(setStageShowMode("")) //out passive aligner
    if (stageShowIpr) {
      dispatch(setOpenStagingIPR(false))
      stagingIPR.switchStagingIPRModule(false)
      dispatch(setStageShowIpr(false))

      // await dispatch(setOpenCollisionCheck(true))
      // gdsPlusSetup.EnterIPREditMode(true)
    }

    dispatch(setStageShowVelocity(false))
    dispatch(setStageQuickFunc(null))
  }, [stageShowIpr])

  const goStaging = (newStageFunc) => {
    // logic to handle the stage function change
    //judge the functions
    if (newStageFunc === "passivealigner") {
      //into passive aligner
      if (stageControl.getMaxStageNodeIndex() > 0) {
        dispatch(setStageShowMode("passive_aligner"))
      } else {
        dispatch(setStageCountZeroWarning(true))
        return
      }
    } else if (newStageFunc === "stageipr") {
      if (stageControl.getMaxStageNodeIndex() > 0) {
        //judge the stage contains passive aligner
        const containsPA = stagingManager.isStageContainsPA(2)
        if (containsPA) {
          dispatch(setStageDeleteAllPAWarning(true))
          return
        }

        const OpenStagingIPR = async () => {
          await dispatch(setOpenInitialFinal(false))
          dispatch(setOpenStagingIPR(true))
          dispatch(setOpenCollisionCheck(false))
          viewControlInTreatment.setViewType("front")
          stagingIPR.switchStagingIPRModule(true)
          stagingIPR.setNumberingSys(NumberSystem)
          dispatch(setStageShowIpr(true))
        }
        OpenStagingIPR()
      } else {
        dispatch(setStageCountZeroWarning(true))
        return
      }
    } else if (newStageFunc === "velocity") {
      if (stageControl.getMaxStageNodeIndex() > 0) {
        dispatch(setStageShowVelocity(true))
      } else {
        dispatch(setStageCountZeroWarning(true))
        return
      }
    }
    // finally set the new stage function
    dispatch(setStageQuickFunc(newStageFunc))
    // dispatch(setOpenCollisionCheck(true))
    // gdsPlusSetup.EnterIPREditMode(true)
  }

  useEffect(() => {
    console.info("🚀 ~ stageQuickFunc:", stageQuickFunc)
  }, [stageQuickFunc])

  useEffect(() => {
    SCCallbackManager.getInstance().setAsyncCheckingBeforeQuitIprCallBack(
      checkingBeforeQuitIpr,
    )
    SCCallbackManager.getInstance().setAsyncQuitStagingCallBack(quitStaging)
  }, [stageShowIpr])

  useEffect(() => {
    return () => {
      console.info("UDQuickStaging dealloc")
      dispatch(setStageQuickFunc(null))
      SCCallbackManager.getInstance().setAsyncCheckingBeforeQuitIprCallBack(
        null,
      )
      SCCallbackManager.getInstance().setAsyncQuitStagingCallBack(null)
    }
  }, [])

  return (
    <RightPage
      title="Staging"
      onClose={handleClose}
      content={
        <>
          <Stack direction="row" spacing={2} sx={{ width: "100%" }}>
            <ToggleButtonGroup
              value={stageQuickFunc}
              exclusive
              onChange={handleStageFucChange}
              sx={{
                gap: 2,
                "& .MuiToggleButtonGroup-grouped": {
                  borderRadius: "8px !important",
                },
              }}
            >
              <ToggleButton
                value="passivealigner"
                sx={{
                  width: "100px",
                  height: "60px",
                  borderRadius: "8px",
                  backgroundColor: "#e0e0e0",
                  "&:hover": {
                    backgroundColor: "#e0e0e0",
                  },
                  "&.Mui-selected": {
                    backgroundColor: "#1E88E5",
                    "& .MuiTypography-root": {
                      color: "#fff",
                    },
                    ":hover": {
                      backgroundColor: "#1E88E5",
                    },
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "rgba(96, 96, 96, 1)",
                    textTransform: "none",
                  }}
                >
                  Passive Aligner
                </Typography>
              </ToggleButton>

              <ToggleButton
                value="stageipr"
                sx={{
                  width: "100px",
                  height: "60px",
                  borderRadius: "8px",
                  backgroundColor: "#e0e0e0",
                  "&:hover": {
                    backgroundColor: "#e0e0e0",
                  },
                  "&.Mui-selected": {
                    backgroundColor: "#1E88E5",
                    "& .MuiTypography-root": {
                      color: "#fff",
                    },
                    ":hover": {
                      backgroundColor: "#1E88E5",
                    },
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "rgba(96, 96, 96, 1)",
                    textTransform: "none",
                  }}
                >
                  Stage IPR
                </Typography>
              </ToggleButton>
              <ToggleButton
                value="velocity"
                sx={{
                  width: "100px",
                  height: "60px",
                  borderRadius: "8px",
                  backgroundColor: "#e0e0e0",
                  "&:hover": {
                    backgroundColor: "#e0e0e0",
                  },
                  "&.Mui-selected": {
                    backgroundColor: "#1E88E5",
                    "& .MuiTypography-root": {
                      color: "#fff",
                    },
                    ":hover": {
                      backgroundColor: "#1E88E5",
                    },
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "rgba(96, 96, 96, 1)",
                    textTransform: "none",
                  }}
                >
                  Velocity
                </Typography>
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </>
      }
    />
  )
}

export default UDQuickStaging
